import React, { useMemo, useState } from "react";
import { SettingsPageLayout } from "newui/features/Settings/components/SettingsPageLayout";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import type { Company } from "newui/features/Company/types";
import { ScoringInstructions } from "./components/ScoringInstructions/ScoringInstructions";
import { EvaluationPermissions } from "./components/EvaluationPermissions/EvaluationPermissions";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { QuoteTemplateSettings } from "newui/features/Rfx/components/QuoteTable/QuoteTemplateSettings";
import { CompanyScheduleSettings } from "../Rfx/components/ScheduleOfRates";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface RfxSettingsProps {
  company: Company;
  path?: string;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const SCORING_INSTRUCTIONS = "Scoring Instructions";
const EVALUATION_PERMISSIONS = "Evaluation Permissions";
const SCHEDULE_OF_RATES = "Schedule of Rates";
const QUOTE_TEMPLATES = "Quote Templates";

const PROCURER_TABS: TabMenuItem[] = [
  {
    id: "scoring-instructions",
    name: SCORING_INSTRUCTIONS,
    showBadge: false,
    total: 0,
  },
  {
    id: "evaluation-permissions",
    name: EVALUATION_PERMISSIONS,
    showBadge: false,
    total: 0,
  },
  {
    id: "quote-templates",
    name: QUOTE_TEMPLATES,
    showBadge: false,
    total: 0,
  },
];

const SUPPLIER_TABS: TabMenuItem[] = [
  {
    id: "schedule-of-rates",
    name: SCHEDULE_OF_RATES,
    showBadge: false,
    total: 0,
  },
];

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const RfxSettings: React.FC<RfxSettingsProps> = ({ company }) => {
  const { isClientProfileActive } = useProfileContext();

  const tabs = useMemo(
    () => (isClientProfileActive ? PROCURER_TABS : SUPPLIER_TABS),
    [isClientProfileActive]
  );

  const defaultTab = tabs[0]?.name ?? SCORING_INSTRUCTIONS;

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const tab = params.get("tab");

  const [currentTab, setCurrentTab] = useState<string>(tab || defaultTab);

  const changeActiveTab = (name: string, _id: string): void => {
    setCurrentTab(name);
  };

  return (
    <SettingsPageLayout
      heading="RFx Settings"
      tabs={tabs}
      activeTab={currentTab}
      onTabChange={changeActiveTab}
    >
      {currentTab === SCORING_INSTRUCTIONS && (
        <ScoringInstructions company={company} />
      )}
      {currentTab === EVALUATION_PERMISSIONS && (
        <EvaluationPermissions company={company} />
      )}
      {currentTab === SCHEDULE_OF_RATES && company?.id && (
        <CompanyScheduleSettings companyId={company.id} />
      )}
      {currentTab === QUOTE_TEMPLATES && company?.id && (
        <QuoteTemplateSettings companyId={company.id} />
      )}
    </SettingsPageLayout>
  );
};
